import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card } from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { cn } from "@loot-os/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  CircleAlert,
  Copy,
  Ellipsis,
  ExternalLink,
  Globe,
  Loader2,
  type LucideIcon,
  Minus,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Star,
  Trash2,
} from "lucide-react";
import { Fragment, type ReactNode, useEffect, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { StorefrontSection } from "../components/StorefrontSection";
import { formatDate } from "../lib/format";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { type trpcClient, useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

// Inferred off the router so the DNS shape stays whatever core returns.
type DomainRow = Awaited<ReturnType<typeof trpcClient.domains.list.query>>[number];
type DnsPlan = NonNullable<DomainRow["dns"]>;
type DnsRecord = DnsPlan["records"][number];
type DomainStatus = DomainRow["status"];
type QuickAddState = Awaited<ReturnType<typeof trpcClient.domains.quickAdd.query>>;

/** How often the list re-polls while a hostname is still being provisioned. */
const POLL_INTERVAL_MS = 10_000;

const STATUS_VARIANT: Record<DomainStatus, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  provisioning: "secondary",
  pending: "secondary",
  failed: "destructive",
  disabled: "outline",
};

function DomainStatusBadge({ status }: { status: DomainStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status]}>
      {status === "provisioning" && <Loader2 className="animate-spin" />}
      {status}
    </Badge>
  );
}

/** The kind of news a status line carries; it picks the icon and the colour. */
type Tone = "ok" | "busy" | "bad" | "idle";

const TONE_ICON: Record<Tone, LucideIcon> = {
  ok: Check,
  busy: Loader2,
  bad: CircleAlert,
  idle: Minus,
};

/** One line of a domain's state: an icon for the kind of news, then the row's own words. */
function StatusLine({ tone, children }: { tone: Tone; children: ReactNode }) {
  const Icon = TONE_ICON[tone];
  return (
    <span
      className={cn(
        "inline-flex items-start gap-1.5 text-xs",
        tone === "bad" && "text-destructive",
        tone === "ok" && "text-foreground",
        (tone === "busy" || tone === "idle") && "text-muted-foreground",
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 size-3 shrink-0",
          tone === "ok" && "text-emerald-500",
          tone === "busy" && "animate-spin",
        )}
      />
      <span>{children}</span>
    </span>
  );
}

/**
 * Where the hostname stands with CloudFront, in the row's words — never raw AWS text. While
 * provisioning, lastError is the loop's note on what it is waiting for (DNS, usually); while
 * pending it says why CloudFront has not accepted the request yet.
 */
function StorefrontStatus({ domain }: { domain: DomainRow }) {
  if (domain.status === "disabled") {
    return (
      <StatusLine tone="idle">
        disabled by platform staff; the storefront does not answer on this hostname
      </StatusLine>
    );
  }
  if (domain.type === "subdomain") {
    return <StatusLine tone="ok">provisioned with the platform; nothing to set up</StatusLine>;
  }
  switch (domain.status) {
    case "active":
      return (
        <StatusLine tone="ok">
          active{domain.verifiedAt ? ` since ${formatDate(domain.verifiedAt)}` : ""}; the
          certificate renews by itself
        </StatusLine>
      );
    case "failed":
      return <StatusLine tone="bad">{domain.lastError ?? "provisioning failed"}</StatusLine>;
    case "provisioning":
      return (
        <StatusLine tone="busy">
          {domain.lastError ?? describeProvisioning(domain.certificateStatus)}
        </StatusLine>
      );
    case "pending":
      return <StatusLine tone="busy">{domain.lastError ?? "not requested yet"}</StatusLine>;
  }
}

/**
 * Where the hostname's own sender (no-reply@ its mail. subdomain, `emailSender` on the row)
 * stands with SES, in the row's words. The storefront sends every email from its primary
 * domain's sender, so the same words differ by whether this row is primary: a verified sender
 * elsewhere is waiting its turn, and a primary still verifying means the platform address for
 * now.
 */
function SenderStatus({ domain }: { domain: DomainRow }) {
  const sender = domain.emailSender;
  if (domain.type !== "custom" || !sender) {
    return (
      <StatusLine tone="idle">
        platform address; a provisioned subdomain has no sender of its own
      </StatusLine>
    );
  }
  switch (domain.emailStatus) {
    case "verified":
      return domain.isPrimary ? (
        <StatusLine tone="ok">the storefront sends as {sender}</StatusLine>
      ) : (
        <StatusLine tone="ok">{sender} verified; used once this domain is primary</StatusLine>
      );
    case "failed":
      return (
        <StatusLine tone="bad">{domain.emailLastError ?? "DKIM verification failed"}</StatusLine>
      );
    case "pending":
      return (
        <StatusLine tone="busy">
          {domain.emailLastError ?? "waiting for the DKIM records"}
          {domain.isPrimary && "; the storefront sends from the platform address until then"}
        </StatusLine>
      );
    default:
      return (
        <StatusLine tone="idle">{domain.emailLastError ?? "sender not requested yet"}</StatusLine>
      );
  }
}

/** True while a reconcile could still move the row: the hostname or its sender is unsettled. */
function isUnsettled(domain: DomainRow): boolean {
  if (domain.type !== "custom" || domain.status === "disabled") return false;
  return domain.status !== "active" || domain.emailStatus !== "verified";
}

function describeProvisioning(status: DomainRow["certificateStatus"]): string {
  switch (status) {
    case "issued":
      return "certificate issued, attaching it and deploying";
    case null:
    case "pending-validation":
      return "waiting for DNS to point here";
    default:
      return `certificate ${status}`;
  }
}

/** Clipboard write with a short "copied" flash for the button that did it. */
function useCopied(): [copied: boolean, copy: (text: string) => void] {
  const [copied, setCopied] = useState(false);
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return [copied, copy];
}

/**
 * A monospace value with a copy button — DNS fields get retyped otherwise. It wraps rather than
 * widening its column: DKIM names and values run past fifty characters.
 */
function CopyableValue({ value }: { value: string }) {
  const [copied, copy] = useCopied();
  return (
    <span className="flex items-start gap-1">
      <code className="min-w-0 break-all font-mono text-xs leading-6">{value}</code>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className="shrink-0 text-muted-foreground"
        aria-label={`Copy ${value}`}
        title={`Copy ${value}`}
        onClick={() => copy(value)}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </span>
  );
}

/**
 * A CAA record's value as the three fields most providers' forms take separately, each with its
 * own copy button, with the one-line zone-file form underneath for providers that have a single
 * field. Shown this way because the whole line pasted into the CA field authorizes no CA at all,
 * and the certificate then fails.
 */
function CaaValue({ fields, line }: { fields: NonNullable<DnsRecord["caa"]>; line: string }) {
  const rows: [label: string, value: string][] = [
    ["Flags", String(fields.flags)],
    ["Tag", fields.tag],
    ["CA domain name", fields.ca],
  ];
  return (
    <div className="flex flex-col">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start gap-2">
          <span className="w-28 shrink-0 text-xs leading-6 text-muted-foreground">{label}</span>
          <CopyableValue value={value} />
        </div>
      ))}
      <div className="break-all pr-7 font-mono text-[11px] leading-4 text-muted-foreground">
        {line}
      </div>
    </div>
  );
}

/** The same records and steps as plain text, for pasting into a ticket or email. */
function formatDnsInstructions(plan: DnsPlan): string {
  const lines = (records: DnsRecord[]) =>
    records.map(
      (record) =>
        `${record.type}${record.typeHint ? ` (${record.typeHint})` : ""}  ${record.name}  ->  ${record.value}  (TTL ${record.ttl})` +
        (record.required ? "" : "  [optional]") +
        (record.caa
          ? `\n    as separate fields: Flags ${record.caa.flags}, Tag ${record.caa.tag}, CA domain name ${record.caa.ca}`
          : "") +
        (record.note ? `\n    ${record.note}` : ""),
    );
  const storefront = plan.records.filter((record) => record.purpose === "storefront");
  const email = plan.records.filter((record) => record.purpose === "email");
  return [
    `Storefront DNS for ${plan.hostname}`,
    "",
    ...lines(storefront),
    ...(email.length > 0
      ? [
          "",
          `Emails from ${plan.emailSender} (optional; used while this is the primary domain)`,
          "",
          ...lines(email),
        ]
      : []),
    "",
    ...plan.steps.map((step, index) => `${index + 1}. ${step}`),
  ].join("\n");
}

/**
 * One group of records, as the four columns every DNS provider's form has. Fixed layout, so the
 * table is exactly as wide as its container and long names and values wrap inside their cells
 * instead of pushing the page out. The relative host is the main name (what most providers'
 * host field wants), the FQDN sits under it; a record's note, when it has one, is the row
 * beneath it, so the condition sits with the record it applies to.
 */
function RecordsTable({ records, notes = true }: { records: DnsRecord[]; notes?: boolean }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table className="table-fixed">
        <colgroup>
          <col className="w-24" />
          <col />
          <col />
          <col className="w-14" />
        </colgroup>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="h-8 text-xs text-muted-foreground">Type</TableHead>
            <TableHead className="h-8 text-xs text-muted-foreground">Name</TableHead>
            <TableHead className="h-8 text-xs text-muted-foreground">Value</TableHead>
            <TableHead className="h-8 text-right text-xs text-muted-foreground">TTL</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => {
            const note = notes ? record.note : undefined;
            return (
              <Fragment key={`${record.type}:${record.name}`}>
                <TableRow className={cn("hover:bg-transparent", note && "border-b-0")}>
                  <TableCell className="align-top whitespace-normal">
                    <div className="flex flex-col leading-6">
                      <span className="font-mono text-xs font-medium">{record.type}</span>
                      {record.typeHint && (
                        <span className="text-xs leading-4 text-muted-foreground">
                          {record.typeHint}
                        </span>
                      )}
                      {!record.required && record.purpose === "storefront" && (
                        <span className="text-xs leading-4 text-muted-foreground">optional</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top whitespace-normal">
                    <CopyableValue value={record.host} />
                    {record.host !== record.name && (
                      <div className="break-all pr-7 font-mono text-[11px] leading-4 text-muted-foreground">
                        {record.name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top whitespace-normal">
                    {record.caa ? (
                      <CaaValue fields={record.caa} line={record.value} />
                    ) : (
                      <CopyableValue value={record.value} />
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right text-xs leading-6 text-muted-foreground">
                    {record.ttl}
                  </TableCell>
                </TableRow>
                {note && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={4}
                      className="whitespace-normal pt-0 text-xs text-muted-foreground"
                    >
                      {note}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * One-step setup through the tenant's DNS provider (Domain Connect). The provider's own page
 * shows the same records as the tables below and adds them on consent, then sends the tenant back
 * here. When it cannot be offered, the reason gets one sentence and the manual records carry on.
 */
function QuickAddPanel({ quickAdd, zone }: { quickAdd: QuickAddState | undefined; zone: string }) {
  if (!quickAdd) return null;
  if (quickAdd.available) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h4 className="text-sm font-medium">One-step setup at {quickAdd.providerDisplayName}</h4>
          <p className="text-xs text-muted-foreground">
            {quickAdd.providerDisplayName} hosts the DNS for{" "}
            <span className="font-mono">{zone}</span>. Continue there to review the records below
            and add them with one click; you come back to this page when it is done. No password or
            API token passes through Loot OS.
          </p>
        </div>
        <Button type="button" onClick={() => window.location.assign(quickAdd.applyUrl)}>
          <ExternalLink />
          Continue to {quickAdd.providerDisplayName}
        </Button>
      </div>
    );
  }
  switch (quickAdd.reason) {
    case "sender_pending":
      return (
        <p className="text-xs text-muted-foreground">
          One-step setup waits for the email sender to be requested, which the next check does; add
          the records below by hand in the meantime.
        </p>
      );
    case "not_onboarded":
      return (
        <p className="text-xs text-muted-foreground">
          {quickAdd.providerDisplayName ?? "This DNS provider"} supports one-step DNS setup, but the
          Loot OS template is not live there yet, so add the records below by hand.
        </p>
      );
    case "apex":
      return (
        <p className="text-xs text-muted-foreground">
          One-step setup covers subdomains only; a zone root takes the ALIAS record below by hand.
        </p>
      );
    default:
      return null;
  }
}

/**
 * The records the tenant adds at their DNS provider, plus the ordered steps. The storefront's
 * own records first; the sender's DKIM records, once the row has them, as an optional group. It
 * sits inside the domain's card, under the two status lines, so the tenant watches the hostname
 * move while the records are still on screen.
 */
function DnsSetupPanel({
  domain,
  quickAdd,
}: {
  domain: DomainRow;
  quickAdd: QuickAddState | undefined;
}) {
  const [copied, copy] = useCopied();
  const plan = domain.dns;
  if (!plan) {
    return (
      <p className="text-xs text-muted-foreground">
        Custom domains are not available on this stage: it has no storefront edge for the tenant to
        point at.
      </p>
    );
  }
  const storefront = plan.records.filter((record) => record.purpose === "storefront");
  const email = plan.records.filter((record) => record.purpose === "email");
  const emailNote = email.find((record) => record.note)?.note;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 basis-64 flex-col gap-1">
          <h3 className="text-sm font-medium">Point {plan.hostname} at this storefront</h3>
          <p className="text-xs text-muted-foreground">
            Add these records in the <span className="font-mono">{plan.zone}</span> zone at the DNS
            provider. Names are relative to that zone, with the full name underneath for providers
            that want it. Keep {plan.hostname} DNS-only (no proxy) until it shows active; from then
            on the certificate renews by itself.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => copy(formatDnsInstructions(plan))}
        >
          {copied ? <Check /> : <Copy />}
          Copy as text
        </Button>
      </div>

      <QuickAddPanel quickAdd={quickAdd} zone={plan.zone} />

      <section className="flex flex-col gap-2">
        <h4 className="text-sm font-medium">Storefront</h4>
        <RecordsTable records={storefront} />
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-medium">
            Emails from <span className="font-mono">{plan.emailSender}</span>
          </h4>
          <p className="text-xs text-muted-foreground">
            {emailNote ??
              "Optional. The three DKIM CNAME records appear here once the sender has been requested, which the next check does. The storefront sends from its primary domain's verified sender, and from the platform address without one."}
          </p>
        </div>
        {email.length > 0 && <RecordsTable records={email} notes={false} />}
      </section>

      <section className="flex flex-col gap-2">
        <h4 className="text-sm font-medium">Steps</h4>
        <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-xs text-muted-foreground">
          {plan.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
    </div>
  );
}

interface DomainCardProps {
  domain: DomainRow;
  /** The DNS setup panel is open under this card. */
  expanded: boolean;
  onToggleDns: () => void;
  /** Asked only for the expanded custom row; undefined otherwise. */
  quickAdd: QuickAddState | undefined;
  isPlatformAdmin: boolean;
  /** Some row mutation is in flight, so every row's actions wait. */
  busy: boolean;
  onRetry: () => void;
  onSetPrimary: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onRemove: () => void;
}

/**
 * One hostname: what it is, where its storefront and its sender stand, and what can be done to
 * it. The things a tenant does while setting up (DNS setup, Check now) are buttons; the rare ones
 * (primary, the staff switch, remove) sit behind the menu.
 */
function DomainCard(props: DomainCardProps) {
  const { domain, expanded, isPlatformAdmin, busy } = props;
  const canSetPrimary = !domain.isPrimary;
  const canRemove = domain.type === "custom";
  const hasMenu = canSetPrimary || isPlatformAdmin || canRemove;
  return (
    <Card size="sm" className="gap-0 py-0">
      <div className="flex flex-wrap items-start justify-between gap-4 p-(--card-spacing)">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-all text-base font-medium">{domain.hostname}</span>
            {domain.isPrimary && (
              <Badge variant="secondary">
                <Star className="fill-amber-400 text-amber-400" />
                Primary
              </Badge>
            )}
            <DomainStatusBadge status={domain.status} />
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-xs text-muted-foreground">Storefront</dt>
            <dd>
              <StorefrontStatus domain={domain} />
            </dd>
            <dt className="text-xs text-muted-foreground">Email sender</dt>
            <dd>
              <SenderStatus domain={domain} />
            </dd>
          </dl>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {domain.type === "custom" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-expanded={expanded}
              onClick={props.onToggleDns}
            >
              <Globe />
              DNS setup
              <ChevronDown
                className={cn("size-3.5 opacity-60 transition-transform", expanded && "rotate-180")}
              />
            </Button>
          )}
          {isUnsettled(domain) && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={props.onRetry}>
              <RefreshCw />
              {domain.status === "failed" || domain.emailStatus === "failed"
                ? "Retry"
                : "Check now"}
            </Button>
          )}
          {hasMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`More actions for ${domain.hostname}`}
                  />
                }
              >
                <Ellipsis />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                {canSetPrimary && (
                  <DropdownMenuItem
                    disabled={busy || domain.status !== "active"}
                    onClick={props.onSetPrimary}
                  >
                    <Star />
                    <span className="flex flex-col">
                      <span>Set as primary</span>
                      <span className="text-xs text-muted-foreground">
                        {domain.status !== "active"
                          ? "only an active domain can be primary"
                          : domain.emailStatus === "verified" && domain.emailSender
                            ? `emails switch to ${domain.emailSender}`
                            : "emails come from the platform address until its sender verifies"}
                      </span>
                    </span>
                  </DropdownMenuItem>
                )}
                {isPlatformAdmin &&
                  (domain.status === "disabled" ? (
                    <DropdownMenuItem disabled={busy} onClick={() => props.onSetEnabled(true)}>
                      <Power />
                      Enable
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem disabled={busy} onClick={() => props.onSetEnabled(false)}>
                      <PowerOff />
                      Disable
                    </DropdownMenuItem>
                  ))}
                {canRemove && (
                  <>
                    {(canSetPrimary || isPlatformAdmin) && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      variant="destructive"
                      disabled={busy}
                      onClick={props.onRemove}
                    >
                      <Trash2 />
                      Remove…
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {domain.type === "custom" && expanded && (
        <div className="border-t p-(--card-spacing)">
          <DnsSetupPanel domain={domain} quickAdd={props.quickAdd} />
        </div>
      )}
    </Card>
  );
}

/**
 * A storefront section: the hostnames are the environment's, like the config they serve, so
 * the page wears the section chrome and `StorefrontLayout` remounts it on an environment
 * switch (the open DNS panel and the remove prompt are one environment's).
 */
export function DomainsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const { query: configQuery } = useStorefrontConfigQuery();
  // Activation is CloudFront's verdict, not a click: only the enable/disable
  // switch is platform-only; everything else is tenant-admin.
  const { isPlatformAdmin } = useTenantRole();
  // The list reconciles stale rows on the way out, so polling it is how the page
  // watches a hostname go from provisioning to active.
  const listOptions = trpc.domains.list.queryOptions(undefined, {
    refetchInterval: (query) => (query.state.data?.some(isUnsettled) ? POLL_INTERVAL_MS : false),
  });
  const domains = useQuery(listOptions);

  const [showAdd, setShowAdd] = useState(false);
  const [hostname, setHostname] = useState("");
  // The domain whose DNS setup is open under its card — set right after an add, so attaching a
  // hostname hands over its records in the same breath. The card reads the list's copy of the
  // row, so the panel follows the polls: the status lines move and DKIM records requested late
  // still appear.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expanded = domains.data?.find((domain) => domain.id === expandedId) ?? null;
  // The remove confirmation: the row stays set through the close animation so the title holds.
  const [removeTarget, setRemoveTarget] = useState<DomainRow | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // storefront.get carries the same domain rows, and the chrome derives the
  // canonical hostname from them (ViewStorefrontButton in TenantLayout). That
  // query stays mounted while this page is open, so without the second
  // invalidation the header keeps the old primary until a reload.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: listOptions.queryKey });
    void queryClient.invalidateQueries({
      queryKey: trpc.storefront.get.queryOptions().queryKey,
    });
  };

  const add = useMutation(
    trpc.domains.add.mutationOptions({
      onSuccess: (domain) => {
        // The new card appears, open on its records, before the list has re-fetched.
        queryClient.setQueryData(listOptions.queryKey, (rows) =>
          rows ? [...rows, domain] : [domain],
        );
        invalidate();
        setShowAdd(false);
        setHostname("");
        setExpandedId(domain.id);
      },
    }),
  );
  const setEnabled = useMutation(
    trpc.platform.domains.setEnabled.mutationOptions({ onSuccess: invalidate }),
  );
  // Check now / Retry: the status lines are the feedback, so no success toast.
  const retry = useMutation(trpc.domains.retry.mutationOptions({ onSuccess: invalidate }));
  const setPrimary = useMutation(
    trpc.domains.setPrimary.mutationOptions({ onSuccess: invalidate }),
  );
  const remove = useMutation(trpc.domains.remove.mutationOptions({ onSuccess: invalidate }));
  const busy = retry.isPending || setEnabled.isPending || setPrimary.isPending || remove.isPending;

  // Asked only while a custom row's DNS setup is open: it does DNS + HTTP discovery.
  const quickAdd = useQuery(
    trpc.domains.quickAdd.queryOptions(
      { domainId: expanded?.id ?? "" },
      { enabled: !!expanded && expanded.type === "custom" && !!expanded.dns, staleTime: 60_000 },
    ),
  );

  // Back from the provider's one-step setup page: say how it went, reconcile the row with its
  // DNS setup open, and drop the query string so a reload does not repeat the toast.
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    connected?: string;
    error?: string;
    error_description?: string;
  };
  useEffect(() => {
    if (!search.connected && !search.error) return;
    if (search.connected) {
      toast.success("Records added at your DNS provider. Activation follows automatically.");
      setExpandedId(search.connected);
      retry.mutate({ domainId: search.connected });
    } else if (search.error) {
      toast.error(
        search.error_description?.startsWith("user_cancel")
          ? "DNS setup was cancelled at the provider."
          : `DNS setup did not complete: ${search.error_description ?? search.error}`,
      );
    }
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        connected: undefined,
        error: undefined,
        error_description: undefined,
      }),
      replace: true,
    });
  }, [search.connected, search.error, search.error_description, retry.mutate, navigate]);

  // Narrowing only: the layout renders a section once the same query has data.
  if (!configQuery.data) return null;
  const { environment } = configQuery.data;

  return (
    <StorefrontSection
      title="Domains"
      description="Hostnames this environment's storefront answers on. A custom domain activates by itself once its DNS points here, with a certificate that is issued and renewed automatically. Every email the storefront sends comes from no-reply@ the mail. subdomain of its primary domain, once that is a custom domain whose three DKIM records have verified; until then from the platform address. Open DNS setup on a domain for the exact records."
      environment={environment}
      aside={
        <Button
          type="button"
          onClick={() => {
            add.reset();
            setShowAdd(true);
          }}
        >
          <Plus />
          Add custom domain
        </Button>
      }
    >
      {domains.data ? (
        <div className="flex flex-col gap-3">
          {domains.data.map((domain) => (
            <DomainCard
              key={domain.id}
              domain={domain}
              expanded={domain.id === expandedId}
              onToggleDns={() => setExpandedId(domain.id === expandedId ? null : domain.id)}
              quickAdd={domain.id === expandedId ? quickAdd.data : undefined}
              isPlatformAdmin={isPlatformAdmin}
              busy={busy}
              onRetry={() => retry.mutate({ domainId: domain.id })}
              onSetPrimary={() => setPrimary.mutate({ domainId: domain.id })}
              onSetEnabled={(enabled) =>
                setEnabled.mutate({ tenantId, domainId: domain.id, enabled })
              }
              onRemove={() => {
                setRemoveTarget(domain);
                setConfirmRemove(true);
              }}
            />
          ))}
        </div>
      ) : domains.error ? (
        <ErrorAlert message={domains.error.message} />
      ) : (
        <Skeleton className="h-28 w-full rounded-xl" />
      )}

      <Dialog open={showAdd} onOpenChange={(open) => !open && setShowAdd(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add custom domain</DialogTitle>
            <DialogDescription>
              Adding the hostname requests its certificate straight away and produces the DNS
              records to hand the tenant. Once those resolve here the domain activates by itself.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate({ hostname: hostname.trim().toLowerCase() });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="domain-hostname">Hostname</Label>
              <Input
                id="domain-hostname"
                type="text"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                placeholder="drops.example.com"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={add.isPending || !hostname.trim()}>
                {add.isPending ? "Adding…" : "Add domain"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.hostname}?</AlertDialogTitle>
            <AlertDialogDescription>
              The storefront stops serving this hostname, its certificate is released and its email
              sender is removed. If it is the primary domain, the provisioned subdomain takes over
              and emails come from the platform address. Remove the DNS records at the provider
              afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (removeTarget) remove.mutate({ domainId: removeTarget.id });
                setConfirmRemove(false);
              }}
            >
              Remove domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StorefrontSection>
  );
}
